/**
 * tokenManager — single source of truth for the access token.
 *
 * ── Token storage strategy ──────────────────────────────────────────────────
 *
 *   Access token  → in-memory only (this module, `let _accessToken`).
 *     • Never written to localStorage, sessionStorage, or any cookie.
 *     • Survives the page lifetime; lost on hard-refresh.
 *     • Rehydrated automatically via /auth/refresh, which reads the httpOnly
 *       refresh-token cookie the browser sends on every same-origin request.
 *     • In-memory storage is invisible to third-party scripts and cannot be
 *       exfiltrated via XSS without full execution-context compromise.
 *
 *   Refresh token → httpOnly cookie set exclusively by the server.
 *     • JS cannot read it (document.cookie does not expose httpOnly cookies).
 *     • Shared across all tabs in the same browser origin — enables the
 *       cross-tab session-restoration flow in SessionExpiredDialog.
 *     • Server cookie flags (see server/routes/auth.js getCookieOptions):
 *         httpOnly: true
 *         secure:   true  (production / HTTPS only)
 *         sameSite: 'lax' (see CSRF note below)
 *         maxAge:   7 days
 *
 * ── Security properties and limitations ─────────────────────────────────────
 *
 *   httpOnly prevents JavaScript from READING the refresh-token cookie.
 *   It does NOT by itself prevent:
 *
 *   1. CSRF (Cross-Site Request Forgery)
 *      The browser will still send httpOnly cookies on cross-origin form posts
 *      or navigations unless SameSite=Strict/Lax is enforced. FluxERP uses:
 *        • SameSite=Lax on the refresh-token cookie (blocks cross-site POSTs).
 *        • A double-submit XSRF-TOKEN pattern: the server sets a readable
 *          XSRF-TOKEN cookie; the client reads it and sends it as the
 *          X-XSRF-TOKEN request header (see api.js request interceptor).
 *          The server validates the header matches the cookie value.
 *        ⚠ Ensure SameSite=Strict is used if state-changing operations are
 *          ever exposed via GET, or if cross-site embedding is not needed.
 *
 *   2. All XSS consequences
 *      httpOnly stops credential theft via document.cookie, but a successful
 *      XSS attack still allows an attacker to:
 *        • Make authenticated API requests from within the victim's tab.
 *        • Read the access token from this module's in-memory variable if the
 *          attack achieves JS execution in the same context.
 *      Mitigations: strict CSP headers, input sanitisation, dependency audits.
 *
 *   3. Token theft via network (without HTTPS)
 *      The `secure` flag ensures cookies are never sent over plain HTTP in
 *      production. Enforce HTTPS everywhere and use HSTS.
 *
 * ── Server-side responsibilities (must remain authoritative) ─────────────────
 *
 *   The client-side session-expiry dialog is a UX layer only. The server must:
 *     • Validate the access token signature and expiry on every protected request.
 *     • Validate the refresh token against the stored hash (auth.js:208) and
 *       reject revoked, rotated, or replayed tokens.
 *     • Enforce tenant, company, and role permissions on every request —
 *       never trust client-supplied context.
 *     • Rotate the refresh token on every /auth/refresh call (token rotation)
 *       to limit the blast radius of a stolen refresh token.
 *     • Implement refresh-token family invalidation: if a previously-consumed
 *       token is presented again, invalidate the entire family.
 */


let _accessToken = null;

const tokenManager = {
  /** Store a fresh access token received from login or refresh. */
  set(token) {
    _accessToken = token;
  },

  /** Return the current in-memory access token (null if not set). */
  get() {
    return _accessToken;
  },

  /** Wipe the in-memory token on logout or auth failure. */
  clear() {
    _accessToken = null;
  },

  /** True only when a non-empty token is held in memory. */
  exists() {
    return !!_accessToken;
  },
};

export default tokenManager;
